/**
 * OwnerSelect.tsx — the owner picklist. Options are the tenant's people from
 * /v1/hr/employees/directory: by default the CURRENT employees (leavers
 * excluded), which is what every WRITE surface wants — a deal or contact is
 * not assigned to someone who has left.
 *
 * Since E360-2 the value is the employee's ID, not their name: the server
 * stores assigned_employee_id (composite FK to employees) and REFUSES a
 * name, so what this control emits is now the only accepted owner wire.
 * Top performers groups by that id, which is what finally stops a rename or
 * a spelling variant from splitting one person into two rows. If the
 * employees read fails the select degrades to "Unassigned" only — never a
 * free-text fallback.
 *
 * §35 (the report builder's owner FILTER) adds two optional props and
 * changes nothing for a mount that passes neither:
 *   includeLeavers — list leavers too, suffixed "(leaver)" on the same row.
 *     A report filtered on a former owner is legitimate and the wire allows
 *     it; hiding the person would make the filter unpickable. The directory
 *     is asked at its cap (500) so the fewest ids fall outside the page.
 *   emptyLabel — what the empty option reads in a FILTER context ("Any
 *     owner"). For a filter, "Unassigned" would claim a population the wire
 *     cannot express: assignedEmployeeId '' is "no filter", not "unowned".
 * With includeLeavers, a value the loaded page does not carry stays
 * selectable as the raw id with a bounded note — the select would otherwise
 * show the empty option while the filter is active. Without includeLeavers
 * the fallback is NOT rendered: a hidden leaver is a hidden leaver, and
 * "not in the directory" would be false of them.
 */
import { useAsync, fetchDirectory } from '../../api'

/** The directory's own cap on a page; asked for whenever leavers are
 *  wanted, and named in the fallback note so the bound is stated. */
const DIRECTORY_CAP = 500

export function OwnerSelect({ value, onChange, className, inheritHint, includeLeavers = false, emptyLabel }: {
  /** The selected employee's ID ('' = unassigned, or no filter). */
  value: string
  onChange: (employeeId: string) => void
  className: string
  /** Convert-modal variant: label the empty option with the inherited
   *  owner instead of "Unassigned" (module-side fallback, §8.D). */
  inheritHint?: string
  /** §35: list leavers too (suffixed), for a FILTER over history. */
  includeLeavers?: boolean
  /** §35: the empty option's text in a FILTER context; inheritHint wins. */
  emptyLabel?: string
}) {
  const employees = useAsync(() => fetchDirectory(includeLeavers ? { limit: String(DIRECTORY_CAP) } : {}), [includeLeavers])
  const people = (employees.data ?? []).filter(e => includeLeavers || e.status !== 'leaver')
  const known = value === '' || people.some(e => e.id === value)
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className={className} disabled={employees.loading}>
      <option value="">
        {employees.loading ? 'Loading people…'
          : employees.error ? (emptyLabel ? 'People unavailable — no owner filter' : 'People unavailable — leave unassigned')
          : inheritHint || emptyLabel || 'Unassigned'}
      </option>
      {people.map(e => (
        <option key={e.id} value={e.id}>{e.fullName}{includeLeavers && e.status === 'leaver' ? ' (leaver)' : ''}</option>
      ))}
      {includeLeavers && !employees.loading && !known && (
        <option value={value}>{value} — not in the directory (first {DIRECTORY_CAP} people)</option>
      )}
    </select>
  )
}
